import * as Icons from '@ant-design/icons';
import { Button, Progress, Space, Table, Tag, Tooltip } from 'antd';
import { CourseNameLink } from 'components/user/CourseNameLink';
import {
  ENROLLMENT_STATUS,
  ENROLLMENT_STATUS_LABELS
} from 'constants/admin/enrollments';
import { EnrollmentStatusFilter } from 'components/user/EnrollmentStatusFilter';
import { HttpError, useNavigation } from '@refinedev/core';
import { IGraduateCourse } from 'interfaces/graduate_courses';
import { IGraduateCourseEnrollment } from 'interfaces/graduate_course_enrollments';
import { TagField, TextField, useTable } from '@refinedev/antd';
import { formatDate } from 'services/date';
import { tagColorByStatus } from 'utils/statusColorMapping';
import { useState } from 'react';

interface Props {
  userId: string;
}

const renderCourseType = (course?: IGraduateCourse) => {
  if (!course) {
    return '-';
  }

  if (course.course_type === 'on_demand') {
    return <Tag color="blue">Flex</Tag>;
  }

  const label =
    course.kind === 'undergraduate' ? 'Graduação Regular' : 'Pós Regular';

  return <Tag color="geekblue">{label}</Tag>;
};

const GraduateCourseEnrollmentTable = ({ userId }: Props) => {
  const navigate = useNavigation();
  const [status, setStatus] = useState<string | undefined>(
    ENROLLMENT_STATUS.ACTIVE
  );

  const { editUrl } = navigate;
  const { tableProps, setFilters, setCurrent } = useTable<
    IGraduateCourseEnrollment,
    HttpError
  >({
    resource: 'graduate/courses/enrollments',
    queryOptions: {
      enabled: Boolean(userId)
    },
    syncWithLocation: false,
    filters: {
      permanent: [
        {
          field: 'filter[by_user_id]',
          operator: 'eq',
          value: userId
        },
        {
          field: 'filter[by_kind][]',
          operator: 'eq',
          value: ['graduate', 'undergraduate']
        }
      ],
      initial: [
        {
          field: 'filter[by_status]',
          operator: 'eq',
          value: ENROLLMENT_STATUS.ACTIVE
        }
      ]
    }
  });

  const handleStatusChange = (nextStatus?: string) => {
    setStatus(nextStatus);
    setCurrent(1);
    setFilters(
      [
        {
          field: 'filter[by_status]',
          operator: 'eq',
          value: nextStatus
        }
      ],
      'merge'
    );
  };

  return (
    <Space direction="vertical" size="middle" className="w-full">
      <EnrollmentStatusFilter value={status} onChange={handleStatusChange} />

      <Table
        {...tableProps}
        rowKey="id"
        bordered
        scroll={{ x: true }}
        className="[&_tr:hover_.course-link-icon]:opacity-100"
      >
        <Table.Column<IGraduateCourseEnrollment>
          dataIndex="course"
          key="course"
          title="Curso"
          render={course => (
            <CourseNameLink
              href={
                course?.id ? editUrl('graduate_courses', course.id) : undefined
              }
              name={course?.name}
            />
          )}
        />
        <Table.Column<IGraduateCourseEnrollment>
          dataIndex="course"
          key="course_type"
          title="Tipo"
          render={course => renderCourseType(course)}
        />
        <Table.Column
          dataIndex="registration"
          key="registration"
          title="Matrícula"
          render={value => <TextField value={value} />}
        />
        <Table.Column
          dataIndex="status"
          key="status"
          title="Status"
          render={status => (
            <TagField
              color={tagColorByStatus(status)}
              value={ENROLLMENT_STATUS_LABELS[status] || status}
            />
          )}
        />
        <Table.Column
          dataIndex="progress_percentage"
          key="progress_percentage"
          title="Progresso"
          render={progress => (
            <Tooltip title={`${progress}%`}>
              <Progress
                percent={progress}
                showInfo={false}
                strokeColor={{
                  '0%': '#0322e2',
                  '100%': '#08adff'
                }}
              />
            </Tooltip>
          )}
        />
        <Table.Column
          dataIndex="created_at"
          key="created_at"
          title="Início"
          render={createdAt => <TextField value={formatDate(createdAt, 'l')} />}
        />
        <Table.Column
          dataIndex="expires_at"
          key="expires_at"
          title="Expiração"
          render={expiresAt => (
            <TextField value={expiresAt && formatDate(expiresAt, 'l')} />
          )}
        />
        <Table.Column<IGraduateCourseEnrollment>
          title="Ações"
          dataIndex="actions"
          align="center"
          render={(_, record) => {
            return (
              <Button
                icon={<Icons.EyeOutlined />}
                size="small"
                onClick={() =>
                  navigate.show(`graduate_enrollment`, record.id, 'push')
                }
              />
            );
          }}
        />
      </Table>
    </Space>
  );
};

export default GraduateCourseEnrollmentTable;
